import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import {
    isOlderThanSixMonths,
    isDocumentDateOutsideReportingPeriod,
    isValidIDOrCompanyID,
    isValidReference,
    shouldWarnFutureDocumentDate,
} from './validationHelpers';
import Constants from '../assests/Constants/constants';
import {
    getSortCodeDocTypeMismatch,
    lookupSortCodeVatType,
} from './sortCodeDocTypeMismatch';
import { shouldWarnMissingAllocationNumber } from './expenseAllocationNumberWarning';
import '../App.css'

const sortCodeMismatchSwalOptions = (mismatch, t, { confirmOnly }) => ({
    title: t('Warning'),
    text: t(mismatch.messageKey),
    icon: 'warning',
    showCancelButton: !confirmOnly,
    confirmButtonText: confirmOnly ? t('OK') : t('YesProceed'),
    cancelButtonText: t('NoCancel'),
    reverseButtons: !confirmOnly,
    heightAuto: false,
    customClass: {
        container: 'swal-high-zindex'
    }
});

/**
 * Informational warning (OK only), same style as the misfiled-document Swal in EditModal.
 */
export function showSortCodeDocTypeMismatchWarning(docType, sortCodeVatType, t) {
    const mismatch = getSortCodeDocTypeMismatch(docType, sortCodeVatType);
    if (!mismatch.isMismatch) return;
    Swal.fire(sortCodeMismatchSwalOptions(mismatch, t, { confirmOnly: true }));
}

/**
 * Blocking warning. Returns false if the user cancels.
 */
export async function confirmSortCodeDocTypeMismatch(docType, sortCodeVatType, t) {
    const mismatch = getSortCodeDocTypeMismatch(docType, sortCodeVatType);
    if (!mismatch.isMismatch) return true;
    const { isConfirmed } = await Swal.fire(
        sortCodeMismatchSwalOptions(mismatch, t, { confirmOnly: false })
    );
    return isConfirmed;
}

/**
 * Validates the document before processing or updating
 * @param {Object} document - The document object (editedItem)
 * @param {Function} t - Translation function
 * @param {Object} client - The client object (to check account management type)
 * @param {boolean} [forceProcess=false] - When true, bypasses sort-code / debit / credit
 *   requirements only (used when the user opts into "Force Process" in the EditModal).
 * @param {Array} [sortCodeOptions=null] - Sort codes, used to warn when VAT type does not match doc type.
 * @returns {boolean} - Returns `true` if validation passes, otherwise `false`
 */
export async function validateDocumentBeforeProcessing(document, t, client, forceProcess = false, sortCodeOptions = null) {
    // ✅ 1) Check if the document date is older than 6 months
    if (isOlderThanSixMonths(document.reportingMonthDate,document.documentDate,client.reportingType)) {
        const { isConfirmed } = await Swal.fire({
            title: t('Warning'),
            text: t('DateOlderThanSixMonths'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('YesProceed'),
            cancelButtonText: t('NoCancel'),
            reverseButtons: true,
            heightAuto: false,
            customClass: {
                container: 'swal-high-zindex'
            }
        });

        if (!isConfirmed) return false;
    }

    // ✅ 1.1) Check if the document date is in the future
    // Bimonthly clients: a date inside the reporting pair (1-2, 3-4, ...) is not a future-date issue
    if (shouldWarnFutureDocumentDate(document.documentDate, document.reportingMonthDate, client?.reportingType)) {
        const { isConfirmed } = await Swal.fire({
            title: t('Warning'),
            text: t('DateInFuture'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('YesProceed'),
            cancelButtonText: t('NoCancel'),
            reverseButtons: true,
            heightAuto: false,
            customClass: {
                container: 'swal-high-zindex'
            }
        });

        if (!isConfirmed) return false;
    }

    // ✅ 1.2) Incomes only: documentDate must fall in the reporting period
    // Monthly → same month; Bimonthly → reporting pair (e.g. Jul/Aug or Sep/Oct)
    if (
        document.docType === 'Incomes' &&
        isDocumentDateOutsideReportingPeriod(
            document.reportingMonthDate,
            document.documentDate,
            client.reportingType
        )
    ) {
        const { isConfirmed } = await Swal.fire({
            title: t('Warning'),
            text: t('DocumentDateOutsideReportingPeriod'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('YesProceed'),
            cancelButtonText: t('NoCancel'),
            reverseButtons: true,
            heightAuto: false,
            customClass: {
                container: 'swal-high-zindex'
            }
        });

        if (!isConfirmed) return false;
    }

    // ✅ 2) Validate Company ID / Israeli ID
    if (!isValidIDOrCompanyID(document.accountCaseId)) {
        toast.error(t('InvalidCompanyOrIDNumber'));
        return false;
    }

    // ✅ 3) Ensure Reference is provided (journal entries may be processed without one)
    const isJournalEntry =
        document.docType === 'JournalEntries' ||
        document.docType === 'JournalEntry';
    if (!isJournalEntry && !isValidReference(document.reference)) {
        toast.error(t('ReferenceIsRequired'));
        return false;
    }
    
    // ✅ 4) Ensure required fields are filled based on account management type
    if (!forceProcess) {
        if (
            client.accountManagement?.trim() === Constants.AccountManagementOptions[0].name
            && (!document.sortCodeId || !document.creditAccount || !document.debitAccount)
        ) {
            toast.info(t('DocumentMustHaveSortCodeCreditDebitAccountToMarkAsProcessed'));
            return false;
        }
        else if (!document.sortCodeId) {
            toast.info(t('DocumentMustHaveSortCodeToMarkAsProcessed'));
            return false;
        }
    }

    // ✅ 5) Income/expense sort-code VAT type must match the document type (journal entries may mix)
    const vatType = lookupSortCodeVatType(document.sortCodeId, sortCodeOptions, document);
    const docType = document.docType || document.documentType;
    const sortCodeMatchesDocType = await confirmSortCodeDocTypeMismatch(docType, vatType, t);
    if (!sortCodeMatchesDocType) return false;

    // ✅ 6) Expense / VAT-liable journal entry missing allocation number above the Israel invoice limit
    const allocationWarning = shouldWarnMissingAllocationNumber(document);
    if (allocationWarning.shouldWarn) {
        const { isConfirmed } = await Swal.fire({
            title: t('Warning'),
            text: t('ExpenseMissingAllocationNumber', {
                limit: allocationWarning.limit.toLocaleString('en-US'),
            }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('YesProceed'),
            cancelButtonText: t('NoCancel'),
            reverseButtons: true,
            heightAuto: false,
            customClass: {
                container: 'swal-high-zindex'
            }
        });

        if (!isConfirmed) return false;
    }

    // ✅ Validation passed
    return true;
}


/**
 * Validates that each Payment Acceptance item has the required fields.
 * @param {Object} document - The Payment Acceptance document object
 * @param {Function} t - Translation function
 * @returns {boolean} - Returns `true` if validation passes, otherwise `false`
 */
export async function validatePaymentAcceptanceItems(document, t, client) {
    
    // 1. Check the top-level date (if your use-case requires it)
    if (isOlderThanSixMonths(document.reportingMonthDate,document.documentDate,client.reportingType)) {
        const { isConfirmed } = await Swal.fire({
            title: t('Warning'),
            text: t('DateOlderThanSixMonths'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('YesProceed'),
            cancelButtonText: t('NoCancel'),
            reverseButtons: true,
            heightAuto: false,
            customClass: {
                container: 'swal-high-zindex'
            }
        });

        if (!isConfirmed) return false;
    }

    // 1.1. Check if the document date is in the future
    if (shouldWarnFutureDocumentDate(document.documentDate, document.reportingMonthDate, client?.reportingType)) {
        const { isConfirmed } = await Swal.fire({
            title: t('Warning'),
            text: t('DateInFuture'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('YesProceed'),
            cancelButtonText: t('NoCancel'),
            reverseButtons: true,
            heightAuto: false,
            customClass: {
                container: 'swal-high-zindex'
            }
        });

        if (!isConfirmed) return false;
    }

    // 2. Validate each checks item
    for (const checkItem of document.checks || []) {
        if (!checkItem.debitAccount || !checkItem.creditAccount) {
            toast.error(t('CheckItemMissingAccount'));
            return false;
        }
        if (!checkItem.paymentDate) {
            toast.error(t('CheckItemMissingPaymentDate'));
            return false;
        }
    }

    // 3. Validate each creditCards item
    for (const card of document.creditCards || []) {
        if (!card.debitAccount || !card.creditAccount) {
            toast.error(t('CreditCardItemMissingAccount'));
            return false;
        }
        if (!card.paymentDate) {
            toast.error(t('CreditCardItemMissingPaymentDate'));
            return false;
        }
    }

    // 4. Validate each cash item
    for (const cashItem of document.cash || []) {
        if (!cashItem.debitAccount || !cashItem.creditAccount) {
            toast.error(t('CashItemMissingAccount'));
            return false;
        }
        if (!cashItem.paymentDate) {
            toast.error(t('CashItemMissingPaymentDate'));
            return false;
        }
    }

    // 5. Validate each bankTransactions item
    for (const bankTrans of document.bankTransactions || []) {
        if (!bankTrans.debitAccount || !bankTrans.creditAccount) {
            toast.error(t('BankTransactionItemMissingAccount'));
            return false;
        }
        if (!bankTrans.paymentDate) {
            toast.error(t('BankTransactionItemMissingPaymentDate'));
            return false;
        }
    }

    // 6. Validate each offsets item
    for (const offset of document.offsets || []) {
        if (!offset.debitAccount || !offset.creditAccount) {
            toast.error(t('OffsetItemMissingAccount'));
            return false;
        }
        if (!offset.paymentDate) {
            toast.error(t('OffsetItemMissingPaymentDate'));
            return false;
        }
    }

    // If all checks passed, return true
    return true;
}